import { useLocation } from 'react-router-dom';
import { tagTypeService } from 'js/services/tagType.service';
import { TAG_TYPE } from 'js/components/system/TagManager/constants';
import { useUserSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAddSubscription, useGetPlayerTags, usePushSubscription } from 'server';
import { useNavigate } from 'js/hooks/navigate';
import { usePrevious } from 'js/hooks/usePrevious';

import * as Types from './types';

export const UserFlowBuilder = ({ config }: Types.IUserFlowBuilder) => {
    const { flows } = config;
    const {
        isSessionStarted,
        windowSessionId,
        isLoggedIn
    } = useUserSelector(pick(['isSessionStarted', 'windowSessionId', 'isLoggedIn']));
    const navigate = useNavigate();
    const addSubscription = useAddSubscription();
    const getPlayerTags = useGetPlayerTags();
    const tagMap = useRef(new Map());
    const { pathname } = useLocation();
    const prevPathname = usePrevious(pathname);
    const tagNames = useMemo(() =>
            flows
                .map(({ tagName }) => tagName)
                .filter((tagName, index, array) => array.indexOf(tagName) === index),
        [
            flows
        ]);

    const processTags = useCallback((type: Types.IUserFlowTriggerCondition) => {
        for (const flow of flows) {
            const tag = tagMap.current.get(flow.tagName);

            switch (flow.redirectionCondition) {
                case Types.IUserFlowRedirectionCondition.PRESENT:
                    if (!tag || flow.tagValue && tag.value !== flow.tagValue) {
                        continue;
                    }

                    break;

                case Types.IUserFlowRedirectionCondition.CHANGED:
                    if (!tag || flow.tagValue && tag.value !== flow.tagValue) {
                        continue;
                    }

                    if (type !== Types.IUserFlowTriggerCondition.PUSH_ADD_CHANGE) {
                        continue;
                    }

                    break;

                case Types.IUserFlowRedirectionCondition.ABSENT:
                    if (!flow.tagValue && tag || flow.tagValue && tag?.value === flow.tagValue) {
                        continue;
                    }

                    break;

                default:
                    continue;
            }

            navigate(flow.pageUrl);

            break;
        }
    }, [
        tagMap,
        navigate,
        flows
    ]);

    useEffect(() => {
        if (!isSessionStarted) {
            return;
        }

        (async () => {
            await addSubscription({
                windowSessionId: windowSessionId ?? -1,
                subscriptionTags: tagNames
            });

            const { tags } = await getPlayerTags({ tagNames });

            for (const tag of tags) {
                tagMap.current.set(tag.name, tag);
            }

            processTags(Types.IUserFlowTriggerCondition.INIT);
        })();
    }, [
        isSessionStarted,
        processTags,
        windowSessionId,
        tagNames,
        getPlayerTags,
        addSubscription
    ]);

    useEffect(() => {
        if (isLoggedIn) {
            return;
        }

        tagMap.current.clear();
    }, [
        isLoggedIn
    ]);

    usePushSubscription(31133, () => (pushMessage) => {
        const { notificationPlayerTagChangeInfo } = pushMessage?.data?.notification || {};

        if (!notificationPlayerTagChangeInfo) {
            return;
        }
        const {
            tag,
            value,
            removed
        } = notificationPlayerTagChangeInfo;

        if (removed) {
            if (tagMap.current.has(tag)) {
                tagMap.current.delete(tag);
            }

            processTags(Types.IUserFlowTriggerCondition.PUSH_REMOVE);
            return;
        }

        tagMap.current.set(tag, {
            name: tag,
            value
        });

        processTags(Types.IUserFlowTriggerCondition.PUSH_ADD_CHANGE);
    });

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        if (prevPathname && pathname !== prevPathname) {
            // check all flows and in case we are on a flow-page then return
            for (const flow of flows) {
                if (flow.pageUrl === pathname) {
                    return;
                }
            }

            // otherwise check our conditions
            processTags(Types.IUserFlowTriggerCondition.NAVIGATION);
        }
    }, [
        isLoggedIn,
        pathname,
        processTags,
        prevPathname,
        flows
    ]);

    return null;
};

tagTypeService.registerTagType({ [TAG_TYPE.userFlowBuilder]: UserFlowBuilder });
